import React from "react";
import CasestudyTemplate, {
  Header,
  Introduction,
  Challenge,
  Body,
} from "../../../../templates/casestudyTemplate";
import Section from "../../../../components/section";
import Gallery from "../../../../components/gallery";

const TLDR = (
  <>
    <p>
      At SSE, a major energy provider, bundled products (energy + additional
      services) were the most valuable for both the company and the customer.
      However, the contract renewal journey failed to highlight these benefits,
      leading to low conversion rates and poor product adoption.
    </p>
    <p>
      SSE’s product portfolio was expanding, but the renewal journey struggled
      to keep up. Key issues included:
    </p>
    <ul>
      <li>
        Poor communication of bundled product benefits, leading to missed upsell
        opportunities.
      </li>
      <li>
        Low conversion rates, with many customers failing to renew their
        contracts.
      </li>
      <li>
        Ineffective user experience, making it difficult for customers to
        compare and select the best tariff.
      </li>
    </ul>
  </>
);

export default function IndexPage() {
  return (
    <CasestudyTemplate id="cx">
      <Header id="cx" />
      <Introduction id="cx">
        <p>
          Today’s digital businesses thrive on great customer experiences. The
          customer experience (CX) management industry is rapidly growing,
          providing companies with deep insights into user behaviour.
        </p>
        <p>
          I joined a fast-moving CX startup that offered eCommerce businesses
          powerful tools for quantitative and qualitative research—including
          pixel-perfect session replay and advanced user behaviour analytics.
          However, rapid growth led to usability challenges, and customers
          struggled to extract meaningful insights. My role was to redefine the
          user experience, making it accessible to a broader range of personas
          across business functions.
        </p>
      </Introduction>
      <Challenge id="cx">
        <p>
          As the company scaled, rapid feature expansion outpaced design
          direction and this resulted in:
        </p>
        <ul>
          <li>
            No clear product strategy – Features were added reactively without a
            cohesive vision
          </li>
          <li>
            Complexity & usability issues – The large feature set created
            confusion, reducing adoption.
          </li>
          <li>
            Competitive pressure – Rival platforms offered a more intuitive
            experience, making it harder to retain and attract customers.
          </li>
        </ul>
        <p>
          To succeed in new markets, we needed a **holistic UX overhaul** that
          made our tool intuitive for a diverse range of business users—not just
          technical analysts.
        </p>
      </Challenge>
      <Body id="cx" TLDR={TLDR}>
        <Section>
          <p>
            As the Senior UX designer I was responsible for building the UX
            function within the business, leading the design direction and
            improving the user experience for our customers.
          </p>
          <h3> Qualitative and quantitative research</h3>
          <p>
            After becoming acquainted with the software I turned my attention to
            research. I wanted to find out first hand from users what goals they
            were trying to achieve and how the were using the software. This
            would give me a good indicator where the pain points were.
          </p>
          <p>
            Google analytics and other sources of quantitative data told me what
            users were doing and when they were doing it but to find out the
            why, I needed to talk to them first hand, get to know them and
            understand what drives them.
          </p>
          <p>
            The logistics of being a designer of one was an interesting
            challenge I needed to solve. To maximise my time with users I
            recorded our conversations (with their permission) and typed up the
            transcripts afterwards. This allowed the conversations to flow more
            naturally and I could focus more on what they were saying rather
            than trying to write everything down.
          </p>
          <p>
            Colour coded highlighting helped me pull out insights from the
            transcripts which lent themselves perfectly to affinity sorting and
            journey mapping.
          </p>
        </Section>
        <Gallery
          id={"cx"}
          images={[
            {
              source: "research1.png",
            },
            {
              source: "research2.png",
            },
            {
              source: "research3.png",
            },
            {
              source: "research4.png",
            },
          ]}
          options={{ grid: 2, width: "full", includeBackground: true }}
        />
        <Section>
          <h3> Ideation and wireframing</h3>
          <p>
            Research highlighted several reoccurring themes and after discussing
            these with stakeholders we prioritised the list and I got to work
            ideating solutions.
          </p>
          <p>
            The biggest challenge was tackling the setup, on-boarding and ease
            of accessing the data for users who didn’t have a technical
            background. This started with brainstorming exercises, early
            sketches and white-boarding which later turning into low fidelity
            wireframes.
          </p>
        </Section>
        <Gallery
          id={"cx"}
          images={[
            {
              source: "wireframe1.png",
            },
            {
              source: "wireframe2.png",
            },
          ]}
          options={{ grid: 2, width: "full", includeBackground: true }}
        />
        <Gallery
          id={"cx"}
          images={[
            {
              source: "wireframe3.png",
            },
            {
              source: "wireframe4.png",
            },
            {
              source: "wireframe5.png",
            },
          ]}
          options={{ grid: 3, width: "inline", includeBackground: false }}
        />
        <Section>
          <h3> Visual design</h3>
          <p>
            One of the main complaints from key stakeholders was that as well as
            being difficult to use, the UI was dated and was a common source of
            criticism from potential customers. To solve this the UI needed a
            complete overhaul.
          </p>
          <p>
            Conducting a UI audit showed me how many inconsistencies there were
            in the UI and illustrated the need for a component library.
          </p>
          <p>
            Working with front end developers we chose Tailwind CSS and Vue.js
            as our front end technologies. This meant we had complete control
            over the components we created and meant the solution could be
            scaled as the product grew.
          </p>
          <p>
            As I was responsible for leading the design, I developed, through
            many iterations, a contemporary style that would be familiar to
            users but also give us the ability to inject some personality into
            the product.
          </p>
        </Section>
        <Gallery
          id={"cx"}
          images={[
            {
              source: "design1.png",
            },
            {
              source: "design2.png",
            },
            {
              source: "design3.png",
            },
            {
              source: "design4.png",
            },
          ]}
          options={{ grid: 2, width: "full", includeBackground: true }}
        />
        <Section>
          <h3> Prototyping, iteration & validation</h3>
          <p>
            To ensure we we were designing the right solutions and that the
            design was intuitive, I conducted regular usability testing with
            users. This almost always included scenarios involving prototypes.
          </p>
          <p>
            My prototyping technique varied in complexity depending on the part
            of the design I was testing.
          </p>
          <p>
            My regular workflow, Sketch + InVision, meant I was able to produce
            prototypes early, rapidly and share them with stakeholders and users
            for quick feedback and iteration. For more complex parts of the
            system I needed to create near realistic prototypes using HTML, CSS,
            PHP and JavaScript. I found this to be useful when dealing with
            complex data structures as I could construct “real world” scenarios
            and have the user perform tasks and manipulate the data as if the
            system were live. This provided more authentic feedback from users
            and gleaned deeper insights than I’d have got from simpler click
            through prototypes.
          </p>
        </Section>
        <Gallery
          id={"cx"}
          images={[
            {
              source: "prototype1.png",
            },
            {
              source: "prototype2.png",
            },
            {
              source: "prototype3.png",
            },
            {
              source: "prototype4.png",
            },
          ]}
          options={{ grid: 4, width: "full", includeBackground: true }}
        />
        <Section>
          <h3> Documentation, design systems and handoff</h3>
          <p>
            Past experience has taught me how messy design files and
            specifications can get. Does v3.2.1_final_final.pdf ring any bells?
          </p>
          <p>
            I wanted to avoid this as much as possible so I worked with front
            end developers to define a better solution. We wanted a
            documentation system that was accessible to everyone and had clear
            guidelines on how and when to use certain components in order to
            speed up development. I also wanted to take away the burden of
            managing inconsistencies from developers so they can focus on doing
            what they do best
          </p>
          <p>
            We opted to use Vuepress, a static site generator. Once design
            components were finalised and passed usability testing, they were
            re-created in Vue.js. These “live” components were then embedded
            into Vuepress and guidelines were written, creating “living”
            documents. The documents became the single source of truth and as
            the components were linked to the live product, if you decided to
            change the colour of say your CTA, every component that used the CTA
            also updated including the instances in your documentation.
          </p>
          <p>
            This method is much slower to begin with but yields great results
            and benefits further down the line as the product scales.
          </p>
        </Section>
        <Gallery
          id={"cx"}
          images={[
            {
              source: "designSystem1.png",
            },
            {
              source: "designSystem2.png",
            },
            {
              source: "designSystem3.png",
            },
          ]}
          options={{ grid: 3, width: "full", includeBackground: true }}
        />
      </Body>
    </CasestudyTemplate>
  );
}
